/**
 * "Standing in the way" — the resolve-class blocker list (QA blocks, red
 * pillars, binding-constraint caps, ELCC conflicts) with each blocker's
 * actionable remediations and the in-place SED-amendment dialog. Rendered by
 * the Decision tab's Decide section (`DecideSurface`).
 *
 * Moved from `pathToGo/ResolveStage.tsx` (Path to GO removal, stage 1). No
 * behaviour changed; test ids are unchanged. Two additions:
 *   - `amendmentReasonSource` names the surface in the amendment's default
 *     audit reason ("Resolved in <source>: …"). It defaulted to the historical
 *     "Path-to-GO" while the Path to GO phase still rendered this list; that
 *     phase was deleted in stage 3, so the default is now "the Decision tab" —
 *     the value DecideSurface already passed. Existing amendment records are
 *     untouched: the reason is stamped when an amendment is written.
 *   - hand-rolled case/letter-spacing utility pairs were dropped where
 *     `type-meta` already sets them (uppercase, 0.1em) — identical render.
 *
 * NOTE: provider-precedence is DETECT-AND-RECORD-ONLY (R-78) — mismatches are
 * recorded to the report's AUDIT section (review-only), never a gate, so no
 * precedence resolution banner is rendered here.
 */
import React from 'react';
import {
  isResolveBlocker,
  isResolveInPlaceStep,
  type Blocker,
  type BlockerSeverity,
} from '../../../lib/pathToGo/blockers';
import type { NextStep } from '../../../lib/pathToGo/nextStep';
import { ResolveAmendmentDialog, type ResolveAmendmentDraft } from './ResolveAmendmentDialog';
import type { SaveAmendmentResult, SEDAmendment } from '../../../types/sedAmendments';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';

/**
 * Blocker-id prefixes that are AUDIT MATTERS: a binding-constraint cap (the
 * Go/No-Go rollup's firing cap) and a firm-capacity (ELCC) conflict are
 * adjudicated against the audit trail, not cleared by a single intent edit.
 * They never render as detail rows — the list collapses them into the
 * counts-and-routes line (Review in decision audit · Open report).
 */
export function isAuditClassBlocker(blocker: Blocker): boolean {
  return (
    blocker.id.startsWith('bindingConstraint:') ||
    blocker.id.startsWith('elcc:') ||
    blocker.id.startsWith('elcc-intent:')
  );
}

const SEVERITY_TONE: Record<BlockerSeverity, string> = {
  critical: 'text-danger',
  significant: 'text-warning',
  noted: 'text-text-muted',
};

const SEVERITY_LABEL: Record<BlockerSeverity, string> = {
  critical: 'Critical',
  significant: 'Significant',
  noted: 'Noted',
};

/* ── An actionable blocker context row ────────────────────────────────── */

/**
 * Renders a resolve-class blocker WITH the actionable remediations it already
 * carries. Each remediation is a `NextStep`; clicking it routes through
 * `onResolveStep(step)` so the host opens the matching flow (the step's
 * `resolver.action` tells the host whether to open Intent or Evidence and which
 * target). Audit-class blockers never reach this row — they are filtered into
 * the counts-and-routes line.
 */
const BlockerRow: React.FC<{
  blocker: Blocker;
  onResolveStep?: (step: NextStep) => void;
  /** RP2 — open the in-place amendment dialog for a resolvable remediation. */
  onResolveInPlaceStep?: (blocker: Blocker, step: NextStep) => void;
  /** Route to the reports tab — the home of audit detail and reruns. */
  onOpenReports?: () => void;
  /** RP3 — true once this blocker was resolved in-place and awaits regeneration. */
  pendingRegeneration?: boolean;
}> = ({
  blocker,
  onResolveStep,
  onResolveInPlaceStep,
  onOpenReports,
  pendingRegeneration,
}) => {
  const remediations = blocker.remediations ?? [];
  return (
    <li
      className="py-2.5 border-b border-border-subtle last:border-b-0"
      data-testid={`resolve-blocker-${blocker.id}`}
      data-severity={blocker.severity}
    >
      <div className="flex items-center gap-2">
        <span
          className={`type-meta px-1.5 py-0.5 rounded-md border border-border-subtle ${SEVERITY_TONE[blocker.severity]}`}
        >
          {SEVERITY_LABEL[blocker.severity]}
        </span>
        <span className="text-[13px] font-bold text-text-primary">{blocker.title}</span>
      </div>
      <div className="text-[11px] text-text-secondary mt-0.5">{blocker.why}</div>
      {/* #26 — specialist-sourcing recommendation: name the specific deliverable
           that clears this binding constraint (mirrors the SED editor's
           "obtain verified value / upload supporting document" affordance). */}
      {blocker.specialistSourcing && (
        <div
          className="text-[11px] text-text-primary mt-1 flex items-start gap-1.5"
          data-testid={`resolve-blocker-sourcing-${blocker.id}`}
        >
          <span className="font-bold text-text-secondary shrink-0">Source:</span>
          <span>{blocker.specialistSourcing.recommendation}</span>
        </div>
      )}

      {/* RP3 — once resolved in-place, the blocker shows a "fix pending
           regeneration" state. Reruns are deliberately NOT offered here
           (William, 2026-08-11): regeneration happens only from the report
           page, so every rerun starts from the report it re-frames. The
           authoritative re-derive drops the row on the next composite refresh;
           this overlay is the optimistic interim. */}
      {pendingRegeneration ? (
        <div
          className="mt-2 rounded-lg border border-primary/40 bg-primary/5 px-2 py-1.5"
          data-testid={`resolve-blocker-pending-${blocker.id}`}
        >
          <div className="type-meta text-text-secondary">
            Resolved at the value source — rerun the analysis from its report page to apply the fix.
          </div>
          {/* The message names the report page as the destination; this is the
              route there (it used to be an instruction with no link). */}
          {onOpenReports ? (
            <button
              type="button"
              onClick={onOpenReports}
              data-testid={`resolve-blocker-open-report-${blocker.id}`}
              className="mt-1.5 inline-flex items-center gap-1 type-meta text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Open report
            </button>
          ) : null}
        </div>
      ) : (
        /* RP1/RP2 — remediations become actionable buttons. A step that maps to
             a concrete SED slot (`resolve-in-place` + sedPath) opens the IN-PLACE
             amendment dialog; every other step keeps the existing nav route. */
        remediations.length > 0 && (
          <div className="mt-2 space-y-1" data-testid={`resolve-blocker-remediations-${blocker.id}`}>
            {remediations.map((step) => {
              const inPlace = isResolveInPlaceStep(step);
              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() =>
                    inPlace && onResolveInPlaceStep
                      ? onResolveInPlaceStep(blocker, step)
                      : onResolveStep?.(step)
                  }
                  disabled={inPlace ? !onResolveInPlaceStep : !onResolveStep}
                  data-testid={`resolve-remediation-${step.id}`}
                  data-resolver-action={step.resolver?.action ?? ''}
                  data-in-place={inPlace ? 'true' : 'false'}
                  className="w-full flex items-start gap-1.5 rounded-lg border border-border-subtle bg-background px-2 py-1.5 text-left type-meta text-text-primary hover:border-border hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60 disabled:cursor-default"
                >
                  <span className="font-bold text-primary shrink-0">{inPlace ? '✎' : '→'}</span>
                  <span className="leading-snug">
                    {inPlace ? `Resolve here: ${step.label}` : step.resolver?.label ?? step.label}
                  </span>
                </button>
              );
            })}
          </div>
        )
      )}
    </li>
  );
};

/* ── SED path → draft hydration ───────────────────────────────────────── */

/** Read a dotted path off the SED data object (best-effort, never throws). */
function readSedPath(
  sed: SiteEvidenceDocument | null | undefined,
  path: string,
): string | number | boolean | null {
  if (!sed) return null;
  const parts = path.split('.');
  let cur: unknown = sed;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return null;
    cur = (cur as Record<string, unknown>)[p];
  }
  if (cur == null) return null;
  if (typeof cur === 'string' || typeof cur === 'number' || typeof cur === 'boolean') return cur;
  return null;
}

/**
 * Build the in-place amendment draft for a resolvable remediation step. Returns
 * null when there is no SED, no usable sedPath, or the SED is not loaded —
 * callers then fall back to the nav route (conservative).
 */
function draftForStep(
  sed: SiteEvidenceDocument | null | undefined,
  blocker: Blocker,
  step: NextStep,
  reasonSource: string,
): ResolveAmendmentDraft | null {
  const sedPath = step.resolver?.sedPath;
  if (!sed || !sedPath || !sedPath.includes('.')) return null;
  const domain = sedPath.split('.')[0] ?? 'general';
  return {
    sedId: sed.id,
    sedVersion: sed.version,
    variablePath: sedPath,
    variableLabel: step.label,
    domain,
    oldValue: readSedPath(sed, sedPath),
    defaultReason: `Resolved in ${reasonSource}: ${blocker.title}`,
    issueType: 'override',
  };
}

/* ── Public list ──────────────────────────────────────────────────────── */

export interface DecisionBlockerListProps {
  blockers: readonly Blocker[];
  /** Open the host flow for a remediation NextStep (intent / evidence editor). */
  onResolveStep?: (step: NextStep) => void;
  /** Open the decision-audit drilldown for an audit-class conflict. */
  onOpenAudit?: (conflictId?: string) => void;
  /** Route to the reports tab — the one home for audit recommendations/hold detail. */
  onOpenReports?: () => void;
  /**
   * RP2 — the live SED, so an in-place-resolvable remediation can hydrate its
   * amendment draft from the current value at `resolver.sedPath`. Absent → the
   * in-place affordance degrades to the nav route (conservative).
   */
  sed?: SiteEvidenceDocument | null;
  /**
   * RP2 — route an in-place amendment through the ONE persist path
   * (host wires this to `saveSEDAmendment`). Absent → in-place resolve is
   * disabled and remediations keep the nav route.
   */
  onResolveInPlace?: (amendment: SEDAmendment) => Promise<SaveAmendmentResult>;
  /** Toast bridge for the in-place dialog. */
  toast?: (msg: string) => void;
  /**
   * The surface named in an in-place amendment's default audit reason
   * ("Resolved in <source>: <blocker>"). Defaults to "the Decision tab".
   */
  amendmentReasonSource?: string;
}

/**
 * The audit-matters counts line + the "Standing in the way" rows + the
 * in-place amendment dialog. Renders nothing visible when no resolve-class
 * blocker is open — the host owns the empty state and the heading.
 */
export const DecisionBlockerList: React.FC<DecisionBlockerListProps> = ({
  blockers,
  onResolveStep,
  onOpenAudit,
  onOpenReports,
  sed,
  onResolveInPlace,
  toast,
  amendmentReasonSource = 'the Decision tab',
}) => {
  // SINGLE SOURCE OF TRUTH: `isResolveBlocker` is the SAME predicate the
  // orchestrator's rail-badge count uses (`countResolveBlockers`).
  const resolveBlockers = blockers.filter(isResolveBlocker);
  // AUDIT MATTERS render as a COUNT with routes, not detail rows (2026-08-22):
  // audit recommendations and hold findings live on the report page; this
  // list navigates to them. Binding-constraint caps and ELCC conflicts are
  // adjudicated against the decision audit, so their severity chips, why copy,
  // and sourcing recommendations are deliberately not rendered here.
  const auditMatters = resolveBlockers.filter(isAuditClassBlocker);
  const actionableBlockers = resolveBlockers.filter((b) => !isAuditClassBlocker(b));

  // RP2/RP3 — in-place dialog + optimistic "pending regeneration" overlay.
  const [dialogDraft, setDialogDraft] = React.useState<ResolveAmendmentDraft | null>(null);
  const [dialogBlockerId, setDialogBlockerId] = React.useState<string | null>(null);
  const [pendingBlockerIds, setPendingBlockerIds] = React.useState<Set<string>>(() => new Set());

  const handleResolveInPlaceStep = React.useCallback(
    (blocker: Blocker, step: NextStep) => {
      const draft = draftForStep(sed, blocker, step, amendmentReasonSource);
      if (!draft || !onResolveInPlace) {
        // No usable SED slot / no persist path → fall back to the nav route.
        onResolveStep?.(step);
        return;
      }
      setDialogBlockerId(blocker.id);
      setDialogDraft(draft);
    },
    [sed, onResolveInPlace, onResolveStep, amendmentReasonSource],
  );

  return (
    <>
      {/* Audit matters — a count with routes. Detail reads on the report page
           and in the decision audit, deliberately not here. */}
      {auditMatters.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border-subtle bg-background-elevated px-3 py-2"
          data-testid="resolve-audit-matters"
          data-count={auditMatters.length}
        >
          <span className="type-meta text-text-secondary">
            {auditMatters.length} audit matter{auditMatters.length === 1 ? ' caps' : 's cap'} this decision.
          </span>
          {onOpenAudit ? (
            <button
              type="button"
              onClick={() => onOpenAudit()}
              data-testid="resolve-audit-matters-open-audit"
              className="type-meta text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Review in decision audit
            </button>
          ) : null}
          {onOpenReports ? (
            <button
              type="button"
              onClick={onOpenReports}
              data-testid="resolve-audit-matters-open-report"
              className="type-meta text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Open report
            </button>
          ) : null}
        </div>
      )}

      {/* Read-only blocker context — QA blocks, red pillars, band. */}
      {actionableBlockers.length > 0 && (
        <div>
          <div className="type-meta text-text-muted font-bold mb-1">
            Standing in the way ({actionableBlockers.length})
          </div>
          <ul
            className="rounded-lg border border-border bg-background-elevated px-3"
            data-testid="resolve-blocker-list"
          >
            {actionableBlockers.map((b) => (
              <BlockerRow
                key={b.id}
                blocker={b}
                onResolveStep={onResolveStep}
                onResolveInPlaceStep={onResolveInPlace ? handleResolveInPlaceStep : undefined}
                onOpenReports={onOpenReports}
                pendingRegeneration={pendingBlockerIds.has(b.id)}
              />
            ))}
          </ul>
        </div>
      )}

      {/* RP2 — the in-place SED-amendment dialog, mounted once. On a successful
           apply the resolved blocker flips to the "pending regeneration" state
           (optimistic; the authoritative re-derive drops it on next compute). */}
      <ResolveAmendmentDialog
        open={dialogDraft != null}
        draft={dialogDraft}
        onResolve={
          onResolveInPlace ??
          (async () => ({ ok: false, error: 'Resolve unavailable' }))
        }
        onClose={() => {
          setDialogDraft(null);
          setDialogBlockerId(null);
        }}
        onResolved={() => {
          if (dialogBlockerId) {
            setPendingBlockerIds((prev) => {
              const next = new Set(prev);
              next.add(dialogBlockerId);
              return next;
            });
          }
        }}
        toast={toast}
      />
    </>
  );
};

export default DecisionBlockerList;
